import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiFile, FiAlertCircle, FiMaximize2, FiMinimize2 } from 'react-icons/fi';
import { Document, Page, pdfjs } from 'react-pdf';
import { getFileTypeInfo, formatFileSize, resolveFileType } from '../../types/dataRoom';
import FileTypeIcon from './FileTypeIcon';

// Configure PDF.js worker — same pattern as MobilePdfViewer.
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const FileIcon = FiFile as React.FC<React.SVGProps<SVGSVGElement>>;
const AlertIcon = FiAlertCircle as React.FC<React.SVGProps<SVGSVGElement>>;
const MaximizeIcon = FiMaximize2 as React.FC<React.SVGProps<SVGSVGElement>>;
const MinimizeIcon = FiMinimize2 as React.FC<React.SVGProps<SVGSVGElement>>;

interface ViewerDocument {
  id: string;
  name: string;
  file_type: string;
  file_size: number;
  /** Optional — used as a fallback when file_type is 'unknown'
   *  (legacy rows uploaded before the MIME→ext fix). */
  original_filename?: string | null;
}

interface DocumentViewerModalProps {
  doc: ViewerDocument;
  // Closures injected by the parent — encapsulate auth differences between
  // owner (Bearer token) and public (token in URL path).
  fetchBytes: () => Promise<ArrayBuffer>;
  fetchOfficeViewerUrl: () => Promise<string>;
  onClose: () => void;
}

const IMAGE_TYPES = ['png', 'jpg', 'jpeg'];
const OFFICE_TYPES = ['docx', 'doc', 'xlsx', 'pptx', 'ppt'];

const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  doc,
  fetchBytes,
  fetchOfficeViewerUrl,
  onClose,
}) => {
  // Resolve via the same helper the badge uses so legacy 'unknown' rows
  // still get routed to the correct render branch.
  const fileType = resolveFileType(doc.file_type, doc.original_filename);
  const isPdf = fileType === 'pdf';
  const isImage = IMAGE_TYPES.includes(fileType);
  const isOffice = OFFICE_TYPES.includes(fileType);
  const typeInfo = getFileTypeInfo(doc.file_type, doc.original_filename);

  // PDF data — kept as ArrayBuffer to feed react-pdf; image blob URL is
  // revoked on unmount to avoid leaking object URLs.
  const [pdfData, setPdfData] = useState<{ data: Uint8Array } | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [officeUrl, setOfficeUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const blobUrlRef = useRef<string | null>(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Fetch on mount based on file type. Each branch is intentionally
  // separate — different render paths need different shapes of data.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError(null);
        if (isPdf) {
          const bytes = await fetchBytes();
          if (!cancelled) {
            // react-pdf accepts { data: Uint8Array } as a stable file source.
            setPdfData({ data: new Uint8Array(bytes) });
          }
        } else if (isImage) {
          const bytes = await fetchBytes();
          if (cancelled) return;
          const mime = fileType === 'png' ? 'image/png' : 'image/jpeg';
          const blob = new Blob([bytes], { type: mime });
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setImageUrl(url);
        } else if (isOffice) {
          const url = await fetchOfficeViewerUrl();
          if (!cancelled) setOfficeUrl(url);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load document for viewing:', err);
          setLoadError('Unable to load this document.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [isPdf, isImage, isOffice, fileType, fetchBytes, fetchOfficeViewerUrl]);

  // Keyboard shortcut interception: Esc closes, but Ctrl/Cmd+S, Ctrl/Cmd+P,
  // and Ctrl/Cmd+Shift+S (Save As) are blocked at the document level so
  // that "Save Page" / "Print to PDF" dialogs cannot be invoked while the
  // modal is open. F12 / DevTools cannot be reliably blocked from JS.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
        return;
      }
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      if (k === 's' || k === 'p' || k === 'a' || (e.shiftKey && k === 's')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', onKey, { capture: true });
    return () => document.removeEventListener('keydown', onKey, { capture: true } as any);
  }, [handleClose]);

  const blockEvent = useCallback((e: React.SyntheticEvent) => {
    e.preventDefault();
  }, []);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  // Page width tracks the viewport so PDFs render at a sensible size on
  // every screen without exposing zoom controls (which would also expose
  // a save button in some browsers).
  const pageWidth = Math.min(900, (containerRef.current?.clientWidth || window.innerWidth) - 40);

  const renderContent = () => {
    if (loading) {
      return (
        <div style={styles.center}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Loading {typeInfo.label}…</p>
        </div>
      );
    }
    if (loadError) {
      return (
        <div style={styles.center}>
          <AlertIcon style={{ width: 36, height: 36, color: '#ef4444', marginBottom: 12 }} />
          <p style={styles.errorText}>{loadError}</p>
        </div>
      );
    }
    if (isPdf && pdfData) {
      return (
        <div ref={containerRef} style={styles.pdfWrap}>
          <Document
            file={pdfData}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            onLoadError={() => setLoadError('Unable to render PDF.')}
            loading={<div style={styles.center}><div style={styles.spinner} /></div>}
          >
            {Array.from(new Array(numPages), (_, i) => (
              <Page
                key={`p-${i}`}
                pageNumber={i + 1}
                width={pageWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            ))}
          </Document>
          {numPages > 0 && (
            <div style={styles.pageBadge}>{numPages} page{numPages === 1 ? '' : 's'}</div>
          )}
        </div>
      );
    }
    if (isImage && imageUrl) {
      return (
        <div style={styles.imageWrap}>
          <img
            src={imageUrl}
            alt={doc.name}
            style={styles.image}
            draggable={false}
            onDragStart={blockEvent}
            onContextMenu={blockEvent}
          />
        </div>
      );
    }
    if (isOffice && officeUrl) {
      // No sandbox: Google Docs Viewer needs full script + cookie access
      // to its own origin to render Office files. Sandboxing here causes
      // a silent blank-page failure with no console error.
      //
      // Google Docs Viewer renders a "popout / open in new window" button
      // in the top-right of its own iframe. That button would expose the
      // underlying signed URL via the new-tab navigation, defeating the
      // view-only guarantee. The iframe is cross-origin so we can't style
      // it — instead we cover the button with an opaque div positioned
      // exactly where Google renders it.
      return (
        <div style={styles.officeWrap}>
          <iframe
            src={officeUrl}
            style={styles.iframe}
            title={doc.name}
          />
          <div style={styles.popoutCover} aria-hidden="true" />
        </div>
      );
    }
    return (
      <div style={styles.center}>
        <FileIcon style={{ width: 36, height: 36, color: '#475569', marginBottom: 12 }} />
        <p style={styles.errorText}>Preview is not available for {typeInfo.label} files.</p>
      </div>
    );
  };

  const modal = (
    <div
      style={styles.overlay}
      onClick={handleOverlayClick}
      onContextMenu={blockEvent}
    >
      <div
        style={isFullscreen ? { ...styles.modal, ...styles.modalFullscreen } : styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={`Viewing ${doc.name}`}
      >
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <FileTypeIcon
              fileType={doc.file_type}
              fallbackFilename={doc.original_filename}
              size={26}
            />
            <span style={styles.docTitle}>{doc.name}</span>
            <span style={styles.docSize}>{formatFileSize(doc.file_size)}</span>
          </div>
          <div style={styles.headerActions}>
            <button
              onClick={() => setIsFullscreen((v) => !v)}
              style={styles.iconBtn}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen
                ? <MinimizeIcon style={{ width: 18, height: 18 }} />
                : <MaximizeIcon style={{ width: 18, height: 18 }} />}
            </button>
            <button onClick={handleClose} style={styles.iconBtn} aria-label="Close viewer">
              <CloseIcon style={{ width: 20, height: 20 }} />
            </button>
          </div>
        </div>
        <div
          style={styles.content}
          onContextMenu={blockEvent}
          onDragStart={blockEvent}
          onCopy={blockEvent}
        >
          {renderContent()}
        </div>
        <div style={styles.viewOnlyTag}>View Access Only</div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10000, padding: 20,
    userSelect: 'none' as const,
    WebkitUserSelect: 'none' as const,
  },
  modal: {
    width: '100%', maxWidth: 1000, height: '90vh',
    backgroundColor: '#1e293b', borderRadius: 12,
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.1)',
    transition: 'all 0.18s ease',
  },
  // Fills the viewport. We override (not concat) maxWidth/height/borderRadius
  // so the modal becomes edge-to-edge and squared off.
  modalFullscreen: {
    maxWidth: '100vw',
    width: '100vw',
    height: '100vh',
    borderRadius: 0,
    border: 'none',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
  },
  headerLeft: {
    display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1,
  },
  typeBadge: {
    padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    color: '#fff', flexShrink: 0,
  },
  docTitle: {
    fontSize: 14, fontWeight: 600, color: '#f1f5f9',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
  },
  docSize: { fontSize: 12, color: '#64748b', flexShrink: 0 },
  headerActions: {
    display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
  },
  iconBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32, borderRadius: 6,
    backgroundColor: 'rgba(255,255,255,0.05)', color: '#94a3b8',
    border: 'none', cursor: 'pointer',
  },
  content: {
    flex: 1, overflow: 'auto', display: 'flex',
    backgroundColor: '#0f172a',
  },
  iframe: {
    width: '100%', height: '100%', border: 'none', backgroundColor: '#fff',
  },
  officeWrap: {
    position: 'relative' as const,
    flex: 1,
    display: 'flex',
  },
  // Sized & positioned to cover Google Docs Viewer's popout button which
  // sits in the top-right of its iframe. Color matches gview's dark grey
  // chrome so the cover blends in instead of looking like a dead pixel.
  popoutCover: {
    position: 'absolute' as const,
    top: 0,
    right: 0,
    width: 64,
    height: 64,
    backgroundColor: '#202124',
    zIndex: 5,
  },
  pdfWrap: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: 20, gap: 12, position: 'relative',
  },
  pageBadge: {
    position: 'sticky', bottom: 8, alignSelf: 'center',
    padding: '4px 10px', borderRadius: 999, fontSize: 12,
    backgroundColor: 'rgba(15,23,42,0.85)', color: '#cbd5e1',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  imageWrap: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'auto', padding: 20,
  },
  image: {
    maxWidth: '100%', maxHeight: '100%',
    objectFit: 'contain' as const,
    pointerEvents: 'none' as const,
  },
  center: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', padding: 40,
    textAlign: 'center' as const,
  },
  spinner: {
    width: 28, height: 28,
    border: '3px solid rgba(255,255,255,0.1)',
    borderTopColor: '#6366f1', borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: { fontSize: 13, color: '#94a3b8', marginTop: 12 },
  errorText: { fontSize: 14, color: '#cbd5e1', maxWidth: 380, lineHeight: 1.5 },
  viewOnlyTag: {
    padding: '6px 12px', fontSize: 11, color: '#94a3b8',
    backgroundColor: 'rgba(15,23,42,0.6)',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    textAlign: 'center' as const, flexShrink: 0,
    letterSpacing: 0.3,
  },
};

export default DocumentViewerModal;
